// Programmatic intent pages — každý intent má unikátní úhel, doporučený formát a FAQ.
// Builder je exportovaný jako mkIntent pro sdílené použití (audiences, formats, comparisons).
export const mkIntent = (i) => ({
  path: `/${i.slug}`,
  type: "intent",
  seo: {
    title: i.title,
    description: i.description,
    schema: ["Service", "BreadcrumbList"],
    ...(i.lang ? { lang: i.lang } : {}),
  },
  h1: i.h1,
  breadcrumbs: [
    { label: "Úvod", href: "/" },
    { label: i.breadcrumb, href: `/${i.slug}` },
  ],
  blocks: [
    {
      t: "hero",
      overline: i.overline,
      title: i.h1,
      sub: i.heroSub,
      trust: ["20–50 lidí", "Ještěd", "ubytování", "soukromý klub", "jeden dodavatel"],
      primary: { label: "Chci připravit misi pro náš tým", href: "/poptavka" },
      secondary: { label: "Zobrazit program", href: "/jak-to-funguje" },
      price: "od 4 990 Kč / osoba",
      img: i.img,
    },
    {
      t: "answer",
      q: i.answerQ,
      short: i.answerShort,
      paras: i.answerDetail,
    },
    ...i.sections.map((s, idx) => ({
      t: "section",
      kicker: s.kicker,
      heading: s.heading,
      paras: s.paras,
      list: s.list,
      img: s.img,
      reverse: idx % 2 === 1,
    })),
    { t: "pricetable" },
    { t: "faq", heading: "Časté otázky", items: i.faq },
    { t: "cta", heading: "Připravíme vám misi", text: "Stačí počet lidí, termín a rozpočet. Návrh mise je nezávazný." },
  ],
  related: i.related,
});

export const INTENTS = [
  mkIntent({
    slug: "outdoor-teambuilding",
    breadcrumb: "Outdoor teambuilding",
    overline: "OUTDOOR TEAMBUILDING",
    title: "Outdoor teambuilding na Ještědu | Mission Ještěd s ubytováním",
    description: "Outdoor teambuilding v Ještědském hřbetu: orientace v terénu, checkpointy, týmové úkoly a finish na Ještědu. S ubytováním a soukromou Warehouse party.",
    h1: "Outdoor teambuilding, který není jen procházka s logem firmy",
    heroSub: "Mapa. Čas. Rozhodnutí. Ještěd. Mission Ještěd je řízená outdoorová mise pro firemní týmy 20–50 lidí — s finishi na hoře a večerem v soukromém klubu.",
    img: "forestMission",
    answerQ: "Co je outdoor teambuilding Mission Ještěd?",
    answerShort: "Řízená 24hodinová týmová mise v Ještědském hřbetu: orientace podle mapy, checkpointy s úkoly, časový limit a finish na Ještědu. Zakončená večeří, noclehem a soukromou party ve Warehouse Liberec.",
    answerDetail: [
      "Na rozdíl od běžných outdoorových aktivit (rafting, paintball, lanovka) má Mission Ještěd příběh a herní design. Účastníci předem neznají trasu ani úkoly — a právě nejistota vytváří skutečnou týmovou dynamiku.",
      "Skóre netvoří jen rychlost: 30 % čas, 40 % splněné úkoly, 20 % týmová spolupráce hodnocená instruktory a 10 % bonusové challenge.",
    ],
    sections: [
      {
        kicker: "TERÉN",
        heading: "Ještědský hřbet jako herní plocha",
        paras: [
          "Mise využívá reálný horský terén: lesní cesty, hřebenové trasy a orientační body s výhledy. Trasa má úrovně obtížnosti, takže ji zvládne sportovec i kolega, co chodí jen po schodech.",
          "Checkpointy kombinují orientaci, komunikaci, leadership a rozhodování pod tlakem. Každý modul má důvod — nic není vycpávka.",
        ],
        img: "qrCheckpoint",
      },
      {
        kicker: "POČASÍ",
        heading: "Outdoor znamená i plán na déšť",
        paras: [
          "Každá mise má weather plan: varianty trasy, kryté checkpointy a v krajním případě indoor backup program ve Warehouse. Extrémní je zážitek, ne riziko.",
          "Sezónní vybavení radíme podle termínu — v průvodci najdete konkrétní checklist, co si vzít na outdoor teambuilding.",
        ],
      },
    ],
    faq: [
      { q: "Je outdoor teambuilding vhodný i pro nesportovce?", a: "Ano. Obtížnost trasy má úrovně a tempo drží tým, ne instruktoři. Fyzicky náročné části lze u jednotlivců vynechat bez vlivu na výsledek týmu." },
      { q: "V jakém ročním období mise běží?", a: "Celoročně. Jaro, léto, podzim i zima mají vlastní varianty trasy a vybavení. Zimní mise má kratší outdoorovou část a delší večerní program." },
      { q: "Co když bude pršet?", a: "Mise má weather plan: zkrácené varianty trasy, kryté checkpointy a indoor backup ve Warehouse. Díky tomu se akce neruší, jen se přizpůsobí." },
    ],
    related: [
      { href: "/mission-jested", title: "Mission Ještěd", text: "Kompletní program mise." },
      { href: "/teambuilding-v-horach", title: "Teambuilding v horách", text: "Horský formát pro firemní týmy." },
      { href: "/pruvodce/co-si-vzit-na-outdoor-teambuilding", title: "Co si vzít na outdoor teambuilding", text: "Checklist vybavení podle sezóny." },
      { href: "/bezpecnost", title: "Bezpečnost", text: "Extreme experience, safe execution." },
    ],
  }),
  mkIntent({
    slug: "adrenalinovy-teambuilding",
    breadcrumb: "Adrenalinový teambuilding",
    overline: "ADRENALINOVÝ TEAMBUILDING",
    title: "Adrenalinový teambuilding pro firmy | Mission Ještěd",
    description: "Adrenalinový firemní teambuilding bez zbytečného rizika: časový tlak, rozhodování, checkpointy a mise na Ještěd. Extrémní je zážitek, ne riziko.",
    h1: "Adrenalinový teambuilding. Extrémní je zážitek, ne riziko.",
    heroSub: "Čas běží, mapa nesedí, tým musí rozhodnout. Mission Ještěd vytváří tlak skutečné mise — bez nebezpečných překážek a improvizace.",
    img: "summit",
    answerQ: "Čím je Mission Ještěd adrenalinová?",
    answerShort: "Časovým limitem, nejistotou a rozhodováním pod tlakem. Účastníci neznají trasu ani úkoly předem — adrenalin vzniká z mise samotné, ne z nebezpečí.",
    answerDetail: [
      "Adrenalin u nás neznamená skoky ze skály. Znamená briefing, kde tým dostane obálku s instrukcemi. Odpočet, který běží pro všechny stejně. Checkpoint, kde dvě trasy vedou k cíli a vy máte třicet vteřin na rozhodnutí.",
      "Celou misi drží instruktoři, nouzový kontakt a weather plan. Bezpečnostní protokol je neměnný — mění se jen obtížnost.",
    ],
    sections: [
      {
        kicker: "GAME DESIGN",
        heading: "Odkud bere mise napětí",
        list: [
          "TIME PRESSURE — časový limit pro všechny týmy",
          "DECISION UNDER PRESSURE — rozhodnutí, která něco stojí",
          "COMMUNICATION — informace má vždy jen část týmu",
          "TRUST — některé úkoly neprojde jednotlivec",
          "ONE TEAM FINISH — mise končí, až je v cíli celý tým",
        ],
        paras: ["Tady nevyhrává nejsilnější. Vyhrává tým."],
      },
      {
        kicker: "BEZPEČNOST",
        heading: "Adrenalin pod kontrolou",
        paras: [
          "Žádné nebezpečné improvizované překážky. Trasa prochází značeným terénem Ještědského hřbetu, instruktoři jsou na checkpointech a nouzový kontakt má každý tým.",
          "Kondici týmu zjišťujeme předem v poptávce a obtížnost ladíme na míru — od managementu po výrobní směny.",
        ],
      },
    ],
    faq: [
      { q: "Není to moc náročné pro běžnou kancelář?", a: "Není. Adrenalin vzniká z časového tlaku a rozhodování, ne z fyzické přetíže. Obtížnost trasy přizpůsobíme kondici týmu." },
      { q: "Co když někdo fyzicky nemůže?", a: "Fyzickou část lze u jednotlivců vynechat. Tým to nijak nepoškodí — čekají ho role na checkpointech i večerní program." },
      { q: "Jak dlouho mise trvá?", a: "Outdoorová část 13:30–17:00. Celý program Mission 24 od arrival v 11:00 do check-outu druhý den v 11:00." },
    ],
    related: [
      { href: "/bezpecnost", title: "Bezpečnost", text: "Jak držíme riziko na nule." },
      { href: "/mission-jested", title: "Mission Ještěd", text: "Kompletní program mise." },
      { href: "/netradicni-teambuilding", title: "Netradiční teambuilding", text: "Mise s příběhem a překvapením." },
      { href: "/cenik", title: "Ceník", text: "Balíčky a orientační ceny." },
    ],
  }),
  mkIntent({
    slug: "netradicni-teambuilding",
    breadcrumb: "Netradiční teambuilding",
    overline: "NETRADIČNÍ TEAMBUILDING",
    title: "Netradiční teambuilding pro firmy | Mission Ještěd",
    description: "Netradiční firemní teambuilding: tajná obálka, mapa, checkpointy a mise na Ještěd, kterou účastníci předem neznají. Konec obyčejným firemním akcím.",
    h1: "Netradiční teambuilding: vaši lidé nebudou vědět, co je čeká",
    heroSub: "Jednoho rána nastoupí do auta. O chvíli později dostanou mapu, několik instrukcí a jediný úkol: dostat celý tým na Ještěd.",
    img: "envelope",
    answerQ: "Čím je Mission Ještěd netradiční?",
    answerShort: "Utajením. Účastníci předem neznají přesný průběh mise — trasu, checkpointy ani úkoly. Vědí jen termín. Zbytek známe my.",
    answerDetail: [
      "Běžný teambuilding je den plný dopředu známých aktivit. Mission Ještěd funguje opačně: čím míň účastníci vědí, tím víc se zapojí. Briefing, obálka, Mission Pack a odpočet vytvářejí zážitek, který firma probírá ještě měsíce potom.",
      "Příběh mise umíme ušit vaší firmě na míru — custom company storyline včetně vašich produktů, hodnot nebo inside jokes.",
    ],
    sections: [
      {
        kicker: "PŘÍBĚH",
        heading: "Mise má scénář. Vy ho nečtete — vy ho žijete.",
        paras: [
          "Od arrival ve Warehouse přes briefing a drop na start až po finish na Ještědu je každá fáze navržená jako kapitola. Účastníci se dozvídají vždy jen to, co potřebují k dalšímu kroku.",
          "Večer ve Warehouse se příběh uzavře: vyhlášení výsledků, ceny a promítání fotografií z mise. A pak party — Warehouse je celý jen pro vás.",
        ],
        img: "mapTable",
      },
      {
        kicker: "PRO HR",
        heading: "Netradiční neznamená neprofesionální",
        paras: [
          "Za zážitkem stojí standardní B2B proces: nabídka na míru, smlouva, fakturace, koordinátor akce a fixní harmonogram. Firma řeší jednoho dodavatele.",
          "Cena je transparentní předem: Mission 24 pro 30 lidí orientačně 169 000 Kč včetně ubytování, jídla a party.",
        ],
      },
    ],
    faq: [
      { q: "Musíme lidem předem prozradit program?", a: "Ne. Stačí jim sdělit termín, místo srazu (nebo odjezd autobusu) a doporučené oblečení. Zbytek je překvapení — a o to jde." },
      { q: "Co když se někdo bojí překvapení?", a: "Koordinátor firmy ví přesný průběh předem a může ho sdělit lidem, kteří to potřebují. Utajení je nástroj, ne podmínka." },
      { q: "Dá se příběh přizpůsobit firmě?", a: "Ano, upsell custom company storyline. Příběh, názvy checkpointů i úkoly můžou navazovat na vaši firmu." },
    ],
    related: [
      { href: "/mission-jested", title: "Mission Ještěd", text: "Kompletní program mise." },
      { href: "/zazitkovy-teambuilding", title: "Zážitkový teambuilding", text: "Mise, hora, party — jeden zážitek." },
      { href: "/jak-to-funguje", title: "Jak to funguje", text: "Pět fází mise krok za krokem." },
      { href: "/cenik", title: "Ceník", text: "Balíčky a orientační ceny." },
    ],
  }),
  mkIntent({
    slug: "zazitkovy-teambuilding",
    breadcrumb: "Zážitkový teambuilding",
    overline: "ZÁŽITKOVÝ TEAMBUILDING",
    title: "Zážitkový teambuilding pro firmy | Mission Ještěd & Warehouse",
    description: "Zážitkový firemní teambuilding, který spojuje outdoor misi na Ještědu, večeři a soukromou party ve Warehouse Liberec. 24 hodin, na které se nezapomíná.",
    h1: "Zážitkový teambuilding: jeden tým, jeden cíl, jedna noc, na kterou se nezapomíná",
    heroSub: "Mission Ještěd spojuje horskou misi, společnou večeři a privátní Warehouse party do 24 hodin, které ukážou, jak váš tým opravdu funguje.",
    img: "finishDrink",
    answerQ: "Co je zážitkový teambuilding Mission Ještěd?",
    answerShort: "24hodinový firemní zážitek v Liberci: řízená outdoorová mise na Ještěd, finish drink na vrcholu, večeře, vyhlášení výsledků a soukromá Warehouse party s DJ až do dvou ráno. Plus nocleh a snídaně.",
    answerDetail: [
      "Zážitkovost netvoří jedna aktivita, ale oblouk: nejistota na startu, napětí na trati, euforie na vrcholu, smích u večeře a tanec do rána. Každá fáze navazuje na předchozí.",
      "Celý zážitek organizuje jeden dodavatel — firma neřeší hotel, autobus, agenturu, venue, catering, bar ani DJ.",
    ],
    sections: [
      {
        kicker: "24 HODIN",
        heading: "Oblouk zážitku od briefing po snídani",
        paras: [
          "11:00 arrival a check-in. 12:30 briefing. 13:30 start mise. 17:00 finish na Ještědu s finish drinkem. 19:30 večeře. 21:00 vyhlášení a promítání fotografií. 21:30–02:00 soukromá party. Ráno snídaně a check-out.",
          "Žádné slepé místo, žádné čekání na dalšího dodavatele. Program teče.",
        ],
        img: "warehouseParty",
      },
      {
        kicker: "FOTO & VIDEO",
        heading: "Zážitek, který zůstane i po akci",
        paras: [
          "Součástí Mission 24 je fotograf po celou akci. Večer se promítají fotografie z mise — a právě ty bývají nejsilnější moment dne.",
          "Jako upsell nabízíme profesionální aftermovie a drone footage dle právních a provozních podmínek.",
        ],
      },
    ],
    faq: [
      { q: "Co všechno je v ceně Mission 24?", a: "Mission Ještěd, 1 noc, doprava v místě, lunch/snack, finish drink, večeře, soukromý Warehouse, DJ, fotograf a snídaně. Cena od 4 990 Kč na osobu." },
      { q: "Je zážitek vhodný pro smíšený tým 20–50 let?", a: "Ano. Obtížnost mise má úrovně, večerní program je společný a každý si najde svoje — od baru po tanec." },
      { q: "Můžeme přidat vlastní program?", a: "Ano. Večer ve Warehouse lze rozšířit o moderátora, live band nebo firemní vyhlášení. Napište nám představu do poptávky." },
    ],
    related: [
      { href: "/mission-jested", title: "Mission Ještěd", text: "Kompletní program mise." },
      { href: "/warehouse-party", title: "Warehouse party", text: "Soukromý klub jen pro vaši firmu." },
      { href: "/teambuilding-s-ubytovanim", title: "Teambuilding s ubytováním", text: "Mise, nocleh a party v jednom." },
      { href: "/cenik", title: "Ceník", text: "Balíčky a orientační ceny." },
    ],
  }),
  mkIntent({
    slug: "vikendovy-teambuilding",
    breadcrumb: "Víkendový teambuilding",
    overline: "VÍKENDOVÝ TEAMBUILDING",
    title: "Víkendový teambuilding pro firmy | Mission Weekend na Ještědu",
    description: "Víkendový firemní teambuilding na Ještědu: Mission Weekend se dvěma noclehy, druhým programem, cateringem a private Warehouse party. Od 6 490 Kč/os.",
    h1: "Víkendový teambuilding, po kterém tým opravdu vypne",
    heroSub: "Mission Weekend: dvě noci, Mission Ještěd, druhý program na míru, catering a soukromá Warehouse party. Firemní víkend, který má strukturu i duši.",
    img: "warehouseExterior",
    answerQ: "Co obsahuje víkendový teambuilding Mission Weekend?",
    answerShort: "Dvě noci ubytování, kompletní Mission Ještěd, druhý program podle vašeho výběru, catering po celou dobu, private Warehouse party s DJ, foto i video a dvě snídaně. Od 6 490 Kč na osobu.",
    answerDetail: [
      "Mission Weekend je navržený pro firmy, které spojují teambuilding s offsite. První den mise, druhý den volnější program — od strategického workshopu po druhou outdoorovou výzvu.",
      "O víkendu je logistika jednodušší: žádné volání z kanceláře, žádné spěchy. Tým je pohromadě dva plné dny.",
    ],
    sections: [
      {
        kicker: "DRUHÝ DEN",
        heading: "Sobotní program stavíme na míru",
        list: [
          "Strategický workshop nebo offsite meeting v prostorách Warehouse",
          "Extra indoor challenge přímo ve Warehouse",
          "Druhá outdoorová aktivita v Ještědském hřbetu",
          "Volný program s cateringem a barem",
        ],
        paras: ["Druhý den není vycpávka. Navrhneme ho podle toho, co má firma ráda — a co tým potřebuje."],
      },
      {
        kicker: "SOBOTA VEČER",
        heading: "Velká soukromá Warehouse party",
        paras: [
          "Sobota patří Warehouse: celý klub jen pro vaši firmu, DJ, bar a jako upsell live band nebo moderátor. Fotky a video z celého víkendu uzavřou večer, který se probírá ještě dlouho.",
        ],
        img: "warehouseParty",
      },
    ],
    faq: [
      { q: "Kolik stojí víkendový teambuilding?", a: "Mission Weekend vychází orientačně od 6 490 Kč na osobu. Finální cena závisí na termínu, počtu lidí a druhém programu." },
      { q: "Lze víkend zkombinovat s rodinami?", a: "Standardní formát je firemní, ale rodinnou variantu umíme probrat individuálně — napište nám do poznámky v poptávce." },
      { q: "Jaký je rozdíl oproti Mission 24?", a: "Mission 24 je 24 hodin s jedním noclehem. Mission Weekend přidává druhou noc, druhý program, catering po celou dobu a foto i video." },
    ],
    related: [
      { href: "/mission-jested", title: "Mission Ještěd", text: "Kompletní program mise." },
      { href: "/cenik", title: "Ceník", text: "Balíčky a orientační ceny." },
      { href: "/teambuilding-pro-management", title: "Teambuilding pro management", text: "Offsite s misí pro vedení firmy." },
      { href: "/teambuilding-s-ubytovanim", title: "Teambuilding s ubytováním", text: "Mise, nocleh a party v jednom." },
    ],
  }),
  mkIntent({
    slug: "jednodenni-teambuilding",
    breadcrumb: "Jednodenní teambuilding",
    overline: "JEDNODENNÍ TEAMBUILDING",
    title: "Jednodenní teambuilding na Ještědu | Mission bez noclehu",
    description: "Jednodenní firemní teambuilding na Ještědu za 4–6 hodin: Mission Ještěd s Mission Packem, checkpointy, finish drinkem a vyhlášením. Od 2 490 Kč/os.",
    h1: "Jednodenní teambuilding: plná mise za 4–6 hodin",
    heroSub: "Mission bez noclehu pro firmy, které chtějí maximum z jednoho dne. Mission Pack, checkpointy, finish drink na Ještědu a závěrečné vyhlášení.",
    img: "qrCheckpoint",
    answerQ: "Co obsahuje jednodenní teambuilding Mission?",
    answerShort: "Kompletní Mission Ještěd bez noclehu: organizace a instruktoři, transport v místě, Mission Pack, checkpointy a týmové úkoly, finish drink na Ještědu, fotografie a závěrečné vyhlášení. Od 2 490 Kč na osobu.",
    answerDetail: [
      "Jednodenní Mission je ideální pro firmy z Liberce, Jablonce nebo Turnova — a pro každého, kdo chce intenzivní zážitek bez logistiky ubytování.",
      "Program trvá 4–6 hodin: briefing, drop na start, mise s checkpointy a finish na Ještědu. Večer lze přidat soukromý Warehouse jako upsell.",
    ],
    sections: [
      {
        kicker: "PROGRAM",
        heading: "Jak vypadá jednodenní mise",
        list: [
          "Briefing a rozdělení do týmů",
          "Organizovaný transport na start (THE DROP)",
          "Mise s checkpointy a týmovými úkoly (3–3,5 h)",
          "Finish na Ještědu: fotografie, finish drink, vyhodnocení",
          "Závěrečné vyhlášení výsledků",
        ],
        paras: ["Žádné zkrácené demo — jednodenní Mission má stejný herní design jako mise s noclehem. Chybí jen večer a snídaně."],
      },
      {
        kicker: "DOPRAVA",
        heading: "Ideální pro firmy z blízkého okolí",
        paras: [
          "Pro Liberec, Jablonec či Turnov je jednodenní formát nejefektivnější: dopoledne v práci, odpoledne mise, večer doma.",
          "Pro vzdálenější firmy (Praha, Hradec) doporučujeme Mission 24 — nebo alespoň náš autobus, aby nikdo neřidičským stylem neřešil cestu zpět.",
        ],
      },
    ],
    faq: [
      { q: "Kolik stojí jednodenní teambuilding?", a: "Mission bez noclehu od 2 490 Kč na osobu. Cena zahrnuje organizaci, transport v místě, Mission Pack, checkpointy, finish drink, fotografie a vyhlášení." },
      { q: "Kdy jednodenní mise začíná a končí?", a: "Standardně arrival 11:00 ve Warehouse, start mise 13:30 a finish na Ještědu kolem 17:00. Harmonogram umíme posunout podle vašich potřeb." },
      { q: "Lze přidat večerní program?", a: "Ano. Soukromý Warehouse s večeří, DJ a barem umíme přidat jako rozšíření — napište nám o něj do poptávky." },
    ],
    related: [
      { href: "/cenik", title: "Ceník", text: "Balíčky a orientační ceny." },
      { href: "/mission-jested", title: "Mission Ještěd", text: "Kompletní program mise." },
      { href: "/teambuilding-liberec", title: "Teambuilding Liberec", text: "Naše domácí základna." },
      { href: "/teambuilding-jablonec-nad-nisou", title: "Teambuilding Jablonec", text: "Pro firmy z Jablonce nad Nisou." },
    ],
  }),
  mkIntent({
    slug: "teambuilding-s-party",
    breadcrumb: "Teambuilding s party",
    overline: "TEAMBULDING S PARTY",
    title: "Teambuilding s party | Mission Ještěd + soukromý Warehouse Liberec",
    description: "Firemní teambuilding s večerní party: Mission Ještěd přes den, večer soukromý Warehouse Liberec jen pro vaši firmu — DJ, bar, party do 02:00.",
    h1: "Teambuilding s party: po misi nekončíte. Warehouse teprve začíná.",
    heroSub: "Odpoledne finish na Ještědu, večer soukromý klub jen pro vaši firmu. DJ, bar, promítání fotografií z mise a party do dvou ráno.",
    img: "warehouseParty",
    answerQ: "Jak vypadá teambuilding s večerní party?",
    answerShort: "Po odpolední Mission Ještěd se tým převeze do Warehouse Liberec — industriálního klubu, který máte celý večer jen pro sebe. Večeře, vyhlášení výsledků, DJ a bar až do 02:00.",
    answerDetail: [
      "Žádné sdílení prostoru s cizími lidmi, žádné fronty u baru, žádný kompromis v muzice. Warehouse je privátní venue — vaše firma je jediný host.",
      "Party je součást balíčku Mission 24 a Mission Weekend. K jednodenní misi ji lze přidat jako rozšíření.",
    ],
    sections: [
      {
        kicker: "WAREHOUSE",
        heading: "Industriální klub, který patří jen vám",
        paras: [
          "Warehouse Liberec je náš vlastní prostor — proto umíme věci, které pronajaté sály neumí. Bar, DJ, světla, promítání fotografií z mise a prostor pro 20–100 lidí.",
          "Vyhlášení výsledků mise ve 21:00 bývá zlomový moment večera: ceny, promítání a první historky z trati. Pak se roztočí DJ.",
        ],
        img: "warehouseExterior",
      },
      {
        kicker: "UPGRADY",
        heading: "Party na míru vaší firmě",
        list: [
          "Open bar — celovečerní otevřený bar",
          "Premium catering a živá stanoviště",
          "Live band místo nebo po DJ setu",
          "Moderátor večera a firemní vyhlášení",
          "Brandovaná trička, medaile a trofeje",
        ],
        paras: [],
      },
    ],
    faq: [
      { q: "Je Warehouse opravdu jen pro nás?", a: "Ano. Na večer vaší akce je Warehouse uzavřený pro veřejnost — firma je jediný host včetně baru a DJ." },
      { q: "Do kolika party trvá?", a: "Standardně 21:30–02:00. Harmonogram večera umíme přizpůsobit — například posunout vyhlášení nebo přidat live band." },
      { q: "Dá se party dokoupit k jednodenní misi?", a: "Ano. Soukromý Warehouse s večeří a DJ umíme přidat k jednodenní Mission jako rozšíření. Napište nám počet lidí a termín." },
    ],
    related: [
      { href: "/warehouse-party", title: "Warehouse party", text: "Detail soukromého klubu pro firmy." },
      { href: "/mission-jested", title: "Mission Ještěd", text: "Kompletní program mise." },
      { href: "/teambuilding-s-ubytovanim", title: "Teambuilding s ubytováním", text: "Mise, nocleh a party v jednom." },
      { href: "/cenik", title: "Ceník", text: "Balíčky a orientační ceny." },
    ],
  }),
  mkIntent({
    slug: "teambuilding-s-dopravou",
    breadcrumb: "Teambuilding s dopravou",
    overline: "TEAMBULDING S DOPRAVOU",
    title: "Teambuilding s dopravou | Autobus od kanceláře na Ještěd",
    description: "Firemní teambuilding s dopravou door-to-door: autobus vyzvedne tým před kanceláří, odveze na Mission Ještěd a po akci vrátí. Žádná logistika na vás.",
    h1: "Teambuilding s dopravou: vyzvedneme vás před kanceláří. A zase vás tam vrátíme.",
    heroSub: "Náš autobus, náš řidič, náš harmonogram. Firma neřeší trasy, parkování ani to, kdo pojede zpět. Prostě nastoupíte — a zbytek je na nás.",
    img: "heroArrival",
    answerQ: "Jak funguje teambuilding s dopravou door-to-door?",
    answerShort: "Objednáme autobus na míru vaší skupině: vyzvedne tým před kanceláří, odveze ho do Warehouse Liberec, obsluhuje přesuny během mise a po check-outu vrátí tým zpět před kancelář.",
    answerDetail: [
      "Doprava je upsell k balíčkům Mission 24 a Mission Weekend — cenu nabídneme v kalkulaci podle vzdálenosti a velikosti skupiny.",
      "Největší výhodu má doprava pro firmy z Prahy, Hradce Králové a Pardubic: cesta se promění v začátek akce, ne v nutné zlo.",
    ],
    sections: [
      {
        kicker: "LOGISTIKA",
        heading: "Proč door-to-door doprava mění celou akci",
        paras: [
          "Nikdo neřídí zpět po náročném dni. Nikdo nehledá parkování. Nikdo neodchází dřív, aby stihl poslední spoj. Tým je pohromadě od první minuty do poslední.",
          "Řidič zná harmonogram mise — odjezdy se ladí s briefingem, startem i check-outem, ne s jízdním řádem.",
        ],
        img: "mapTable",
      },
      {
        kicker: "PŘÍKLAD",
        heading: "Typický časový plán pro firmu z Prahy",
        paras: [
          "9:30 odjezd od kanceláře. 11:00 arrival ve Warehouse Liberec a check-in. 13:30 start mise. 17:00 finish na Ještědu. 19:30 večeře. 21:30 soukromá party. Druhý den 11:00 check-out, kolem oběda jste zpátky v Praze.",
          "Dojezd z Prahy je orientačně 1–1,5 hodiny podle aktuální dopravy po D10.",
        ],
      },
    ],
    faq: [
      { q: "Kolik stojí autobus?", a: "Cena dopravy závisí na vzdálenosti, velikosti skupiny a počtu jízd. Nabídneme ji v rámci nezávazné kalkulace — napište nám, odkud jedete." },
      { q: "Můžeme jet vlastními auty?", a: "Samozřejmě. Parkování je přímo u Warehouse. Ale pro plný zážitek doporučujeme autobus — večer nikdo neřídí." },
      { q: "Zajistíte dopravu i z menších měst?", a: "Ano. Autobus objednáme pro jakoukoliv adresu v ČR. U vzdálenějších lokalit doporučujeme Mission 24 nebo Weekend." },
    ],
    related: [
      { href: "/teambuilding-praha", title: "Teambuilding Praha", text: "Pro firmy z Prahy včetně dopravy." },
      { href: "/mission-jested", title: "Mission Ještěd", text: "Kompletní program mise." },
      { href: "/cenik", title: "Ceník", text: "Balíčky a orientační ceny." },
      { href: "/teambuilding-s-ubytovanim", title: "Teambuilding s ubytováním", text: "Mise, nocleh a party v jednom." },
    ],
  }),
  mkIntent({
    slug: "teambuilding-v-horach",
    breadcrumb: "Teambuilding v horách",
    overline: "TEAMBULDING V HORÁCH",
    title: "Teambuilding v horách | Mission Ještěd v Ještědském hřbetu",
    description: "Firemní teambuilding v horách: Mission Ještěd využívá reálný horský terén Ještědského hřbetu — orientace, checkpointy a finish na vrcholu Ještědu.",
    h1: "Teambuilding v horách, kde hora není kulisa, ale cíl",
    heroSub: "Ještěd není jen pozadí na fotku. Je to finish vaší mise — a vaše logo na týmové fotce pod vysílačem bude letos nejlepší firemní obrázek.",
    img: "summit",
    answerQ: "Jak probíhá teambuilding v horách na Ještědu?",
    answerShort: "Mission Ještěd je řízená horská mise: týmy dostanou mapu a Mission Pack, procházejí checkpointy v Ještědském hřbetu a společně finišují na vrcholu Ještědu (1 012 m).",
    answerDetail: [
      "Horský terén dodává misi autenticitu: počasí, výška a les jsou skutečné. Zároveň ale trasa vede značeným terénem a obtížnost má úrovně — hory u nás neznamenají riziko.",
      "Sezóna hraje roli: léto nabízí dlouhý den, podzim mlhu a barvy, zima kratší outdoorovou část a silnější večerní program.",
    ],
    sections: [
      {
        kicker: "VYBAVENÍ",
        heading: "Co na horský teambuilding vzít",
        paras: [
          "Základ je pevná obuv, vrstvené oblečení a pláštěnka. Detailní checklist podle sezóny jsme sepsali v průvodci — účastníkům ho před akcí rozešleme.",
          "Mission Pack (mapa, výstroj, instrukce) dostává každý tým od nás. S sebou stačí běžná outdoorová výbava.",
        ],
        img: "forestMission",
      },
      {
        kicker: "POČASÍ",
        heading: "Weather plan je součást každé mise",
        paras: [
          "Před každou akcí sledujeme předpověď a připravujeme varianty: zkrácená trasa, kryté checkpointy, indoor backup ve Warehouse. Akce se neruší — přizpůsobí se.",
          "Bezpečnost má přednost před dramaturgií. Extrémní je zážitek, ne riziko.",
        ],
      },
    ],
    faq: [
      { q: "Jak vysoký je Ještěd a jak náročný je výstup?", a: "Ještěd má 1 012 m n. m. Trasa mise má úrovně obtížnosti — standardní variantu zvládne běžný nesportovec v dobrém tempu týmu." },
      { q: "Funguje mise i v zimě?", a: "Ano. Zimní varianta má kratší outdoorovou část, upravené checkpointy a delší program ve Warehouse. Vybavení doporučíme podle termínu." },
      { q: "Co když někdo nemá horskou výbavu?", a: "Postačí pevná obuv a vrstvené oblečení. Speciální výstroj (mapy, potřeby pro checkpointy) zajišťujeme my v rámci Mission Packu." },
    ],
    related: [
      { href: "/outdoor-teambuilding", title: "Outdoor teambuilding", text: "Mise v přírodě Ještědského hřbetu." },
      { href: "/pruvodce/co-si-vzit-na-outdoor-teambuilding", title: "Co si vzít na outdoor teambuilding", text: "Checklist vybavení podle sezóny." },
      { href: "/bezpecnost", title: "Bezpečnost", text: "Extreme experience, safe execution." },
      { href: "/mission-jested", title: "Mission Ještěd", text: "Kompletní program mise." },
    ],
  }),
  mkIntent({
    slug: "teambuilding-pro-management",
    breadcrumb: "Teambuilding pro management",
    overline: "TEAMBULDING PRO MANAGEMENT",
    title: "Teambuilding pro management | Offsite s misí na Ještědu",
    description: "Teambuilding pro management a vedení firmy: Mission Ještěd jako leadership simulace — rozhodování pod tlakem, role, komunikace. S offsite prostorem ve Warehouse.",
    h1: "Teambuilding pro management: 24 hodin, které ukážou, jak vaše vedení opravdu funguje",
    heroSub: "Mission Ještěd není animační program. Je to simulace rozhodování pod tlakem — a večer ve Warehouse prostor pro to, co se do boardroomu nevešlo.",
    img: "mapTable",
    answerQ: "Proč Mission Ještěd pro management?",
    answerShort: "Protože mise testuje přesně to, co dělá vedení firmy: rozhodování pod tlakem, alokaci rolí, komunikaci a zodpovědnost za výsledek celého týmu.",
    answerDetail: [
      "Moduly DECISION UNDER PRESSURE a LEADERSHIP jsou navržené tak, aby odhalily skutečné vzorce: kdo přebírá iniciativu, kdo ustoupí, kdo komunikuje pod tlakem.",
      "Mission Weekend umíme zkombinovat se strategickým workshopem v prostorách Warehouse — dopoledne strategie, odpoledne mise.",
    ],
    sections: [
      {
        kicker: "LEADERSHIP",
        heading: "Role se během mise mění",
        paras: [
          "Na checkpointech rotuje vedení týmu. CEO najednou plní instrukce projektové manažerky — a přesně v těchto momentech se ukazuje kultura firmy.",
          "Instruktoři hodnotí týmovou spolupráci (20 % skóre) a večer při vyhlášení zpětnou vazbu zprostředkují jako příběh mise, ne jako hodnocení lidí.",
        ],
        img: "forestMission",
      },
      {
        kicker: "OFFSITE",
        heading: "Warehouse jako prostor pro vedení",
        paras: [
          "Industriální prostor Warehouse funguje i jako netradiční zasedačka: prezentace, workshop, večeře a soukromý večer v jednom místě.",
          "Pro management doporučujeme Mission Weekend — první den strategie, druhý mise, večer uzavřený privátní party.",
        ],
      },
    ],
    faq: [
      { q: "Je program vhodný pro C-level?", a: "Ano. Program je dospělý, diskrétní a bez infantilních aktivit. Warehouse je privátní prostor a harmonogram držíme pod kontrolou." },
      { q: "Lze kombinovat misi s interním programem?", a: "Ano. Prostor pro workshop nebo prezentaci zajistíme přímo ve Warehouse — typicky dopoledne strategie, odpoledne mise." },
      { q: "Jak velký management tým zvládnete?", a: "Mise funguje od 10 lidí (2 týmy po 5) do 100. Pro menší vedení umíme připravit formát jednoho týmu s rozšířeným debriefingem." },
    ],
    related: [
      { href: "/pro-firmy", title: "Pro firmy", text: "Proč firmy volí Extreme Teambuilding." },
      { href: "/vikendovy-teambuilding", title: "Víkendový teambuilding", text: "Mission Weekend se dvěma noclehy." },
      { href: "/mission-jested", title: "Mission Ještěd", text: "Kompletní program mise." },
      { href: "/cenik", title: "Ceník", text: "Balíčky a orientační ceny." },
    ],
  }),
  mkIntent({
    slug: "teambuilding-pro-obchodni-tym",
    breadcrumb: "Teambuilding pro obchodní tým",
    overline: "TEAMBULDING PRO OBCHODNÍ TÝM",
    title: "Teambuilding pro obchodní tým | Mission Ještěd",
    description: "Teambuilding pro obchodníky: Mission Ještěd promění konkurenceschopnost vašeho sales týmu v týmovou výhru. Scoring, checkpointy, finish a večerní party.",
    h1: "Teambuilding pro obchodní tým, kde soutěživost konečně pracuje pro tým",
    heroSub: "Obchodníci umí soutěžit. Mission Ještěd je naučí soutěžit spolu — skóre netvoří nejrychlejší jednotlivec, ale tým, který funguje jako celek.",
    img: "finishDrink",
    answerQ: "Proč je Mission Ještěd ideální pro obchodní tým?",
    answerShort: "Protože scoring je navržený proti individualismu: 30 % čas, 40 % splněné úkoly, 20 % týmová spolupráce a 10 % bonusy. Vyhrát může jen tým, který nikoho nenechá pozadu.",
    answerDetail: [
      "Sales týmy bývají plné lidí zvyklých vyhrávat samy. Mise obrací pravidla: ONE TEAM FINISH znamená, že čas týmu počítá poslední člen v cíli.",
      "Večerní vyhlášení ve Warehouse pak funguje jako malý firemní galavečer — ceny, promítání fotografií a historky z trati.",
    ],
    sections: [
      {
        kicker: "SCORING",
        heading: "Skóre, které učí spolupráci",
        list: [
          "30 % — čas na trati",
          "40 % — kvalita splněných úkolů",
          "20 % — týmová spolupráce hodnocená instruktory",
          "10 % — bonusové challenge pro odvážné",
        ],
        paras: ["Tady nevyhrává nejsilnější. Vyhrává tým — a to je message, kterou si obchodníci odnesou i do pipeline."],
      },
      {
        kicker: "KICKOFF",
        heading: "Ideální jako kickoff nového období",
        paras: [
          "Spojení mise s večerní party dělá z Mission 24 silný formát pro kickoff kvartálu nebo oslavu uzavřeného roku. Program umíme spojit s interním vyhlášením výsledků firmy.",
          "Pro sales konference s větším počtem lidí zvládneme až 100 účastníků ve vlnových startech.",
        ],
      },
    ],
    faq: [
      { q: "Umíte do programu zakomponovat firemní vyhlášení?", a: "Ano. Večerní program ve Warehouse lze rozšířit o vaše interní vyhlášení, moderátora i vlastní kategorie cen." },
      { q: "Jsme extrémně soutěživí — nebude to problém?", a: "Naopak. Soutěživost je palivo mise. Scoring je ale nastavený tak, aby odměňoval spolupráci, ne individuální výkon." },
      { q: "Kolik stojí akce pro obchodní tým 25 lidí?", a: "Mission 24 pro 25 lidí vychází orientačně na 149 000 Kč včetně ubytování, jídla, party a fotografa." },
    ],
    related: [
      { href: "/pro-firmy", title: "Pro firmy", text: "Proč firmy volí Extreme Teambuilding." },
      { href: "/mission-jested", title: "Mission Ještěd", text: "Kompletní program mise." },
      { href: "/teambuilding-pro-25-lidi", title: "Teambuilding pro 25 lidí", text: "Logistika a rozpočet pro 25 lidí." },
      { href: "/cenik", title: "Ceník", text: "Balíčky a orientační ceny." },
    ],
  }),
  mkIntent({
    slug: "teambuilding-pro-it-firmy",
    breadcrumb: "Teambuilding pro IT firmy",
    overline: "TEAMBULDING PRO IT FIRMY",
    title: "Teambuilding pro IT firmy | Mission Ještěd",
    description: "Teambuilding pro IT týmy a softwarové firmy: Mission Ještěd kombinuje logiku, šifry a komunikaci v terénu. Offline den, který váš dev tým potřebuje.",
    h1: "Teambuilding pro IT firmy: jediný den, kdy váš tým debuguje offline",
    heroSub: "Šifry, logické úkoly, orientace bez GPS a komunikace bez Slacku. Mission Ještěd je přesně ten typ výzvy, který vývojáře baví — a nudí je hotelové aktivity.",
    img: "qrCheckpoint",
    answerQ: "Proč Mission Ještěd pro IT tým?",
    answerShort: "Protože úkoly na checkpointech jsou logické, ne animační. Šifry, optimalizační problémy a komunikační protokoly v terénu — přesně to, co IT mozky baví, bez jediné obrazovky.",
    answerDetail: [
      "QR checkpointy, šifrované instrukce a úkoly s jedním správným řešením (a pěti lákavými špatnými) drží IT tým ve flow celou trasu.",
      "Večer ve Warehouse má DJ a bar IT standard — žádná nucená zábava, prostor pro lidi i pro introverty.",
    ],
    sections: [
      {
        kicker: "GAME DESIGN",
        heading: "Úkoly psané pro analytické mozky",
        paras: [
          "Checkpointy kombinují orientaci, logiku a komunikaci. Informace má vždy jen část týmu — jako v každém správném systému. Bez předávání zpráv se nepohnete dál.",
          "Scoring odměňuje kvalitu řešení (40 %) víc než rychlost (30 %). Dobrý tým přemýšlí, ne běží.",
        ],
        img: "envelope",
      },
      {
        kicker: "PROČ NE ONLINE",
        heading: "Váš tým řeší problémy denně. Ale nikdy společně a pěšky.",
        paras: [
          "Distributed týmy a hybridní práce znamenají, že se lidé znají hlavně z callů. Mise je překlopí do terénu: stejné mozky, jiné prostředí, jiná dynamika.",
          "Večer a nocleh v jednom místě dělají z offsite akce to, co retrospektiva nikdy nesplní.",
        ],
      },
    ],
    faq: [
      { q: "Pracujeme remote z celé republiky — kam se sjedeme?", a: "Nejjednodušší je sraz přímo ve Warehouse Liberec, případně společný autobus z Prahy. Logistiku navrhneme podle toho, odkud lidé jedou." },
      { q: "Budou úkoly dost chytré pro vývojáře?", a: "Ano. Moduly šifer a logiky mají obtížnost nastavitelnou — pro IT týmy nasazujeme náročnější varianty." },
      { q: "Je v ceně Wi-Fi a prostor pro práci?", a: "Warehouse má kompletní zázemí včetně prostoru pro prezentace. Pro hackathon-like formát ráno a misi odpoledne napište nám do poptávky." },
    ],
    related: [
      { href: "/netradicni-teambuilding", title: "Netradiční teambuilding", text: "Mise s příběhem a překvapením." },
      { href: "/pro-firmy", title: "Pro firmy", text: "Proč firmy volí Extreme Teambuilding." },
      { href: "/mission-jested", title: "Mission Ještěd", text: "Kompletní program mise." },
      { href: "/cenik", title: "Ceník", text: "Balíčky a orientační ceny." },
    ],
  }),
];
